export const CONTAINS_FILTER_PREFIX = "__contains__:";
/** Display sentinel for a missing value. Column filters select this for Blank. */
export const EMPTY_FILTER_VALUE = "(Empty)";
/** Filter token for rows whose displayed value is present. */
export const NOT_BLANK_FILTER_VALUE = "__not_blank__";

export function isBlankDisplayValue(value: string | null | undefined): boolean {
  const text = (value ?? "").trim();
  return text.length === 0 || text === EMPTY_FILTER_VALUE;
}

/** Badge text for a blank or not-blank token, when `value` is one of those. */
export function presenceFilterLabel(value: string): string | null {
  if (value === EMPTY_FILTER_VALUE || value === "blank") return "Blank";
  if (value === NOT_BLANK_FILTER_VALUE) return "Not blank";
  return null;
}

export function withoutPresenceFilterValues(
  values: readonly string[],
): string[] {
  return values.filter(
    (value) => value !== EMPTY_FILTER_VALUE && value !== NOT_BLANK_FILTER_VALUE,
  );
}

export function encodeContainsFilterValue(value: string): string {
  return `${CONTAINS_FILTER_PREFIX}${value}`;
}

export function decodeContainsFilterValue(value: string): string | null {
  return value.startsWith(CONTAINS_FILTER_PREFIX)
    ? value.slice(CONTAINS_FILTER_PREFIX.length)
    : null;
}

export function displayValuesEqual(left: string, right: string): boolean {
  return left.localeCompare(right, undefined, { sensitivity: "accent" }) === 0;
}

export function displayValueInList(
  values: readonly string[],
  value: string,
): boolean {
  return values.some((candidate) => displayValuesEqual(candidate, value));
}

/** Question-list columns whose displayed value is always present. */
export const QUESTION_LIST_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "id",
  "scope",
  "license_type_name",
  "active",
  "display_order",
  "answer_type",
  "answer_required",
  "display_up_front",
  "show_on_fees_payment_screen",
  "select_from_fixed_list",
  "copy_value_to_clipboard",
  "allow_multiple_photos",
  "allow_multiple_urls",
]);

/** Drop Blank / Not blank when this column's values are always filled in. */
export function withoutBlankFilterChoices<
  T extends { allowBlankFilter?: boolean },
>(
  accessorKey: string,
  alwaysFilledKeys: ReadonlySet<string>,
  properties: T,
): T {
  if (!alwaysFilledKeys.has(accessorKey)) return properties;
  return { ...properties, allowBlankFilter: false };
}

export function displayValueMatchesFilters(
  displayValue: string,
  filterValues: readonly string[],
): boolean {
  return filterValues.some((filterValue) => {
    if (filterValue === NOT_BLANK_FILTER_VALUE) {
      return !isBlankDisplayValue(displayValue);
    }
    const containsValue = decodeContainsFilterValue(filterValue);
    if (containsValue !== null) {
      return displayValue
        .toLocaleLowerCase()
        .includes(containsValue.toLocaleLowerCase());
    }
    return displayValuesEqual(displayValue, filterValue);
  });
}
